import { fetch_risk_frameworks_by_project } from '@/services/risk_service.js';

function to_array(value) {
  return Array.isArray(value) ? value : [];
}

function first_present(values = []) {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

function format_date(value) {
  if (!value) {
    return 'Not available';
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : new Intl.DateTimeFormat('en-ZA', { dateStyle: 'medium' }).format(date);
}

function normalise_framework_source(framework = {}, index = 0) {
  return {
    framework,
    framework_type: framework.risk_framework_type || {},
    project: framework.project,
    fallback_id: index + 1
  };
}

function map_rule_conditions(source) {
  return {
    type_label: source.framework_type.risk_type || 'General',
    phase_label: source.framework.assessment_phase || 'Not set'
  };
}

function map_rule_actions(source) {
  const project = source.project;

  return {
    project_label: project_label(source.framework, project)
  };
}

function project_label(framework = {}, project) {
  return first_present([
    framework.project_name,
    project?.project_name,
    project?.name,
    project_id_label(project),
    'Tenant-wide'
  ]);
}

function project_id_label(project) {
  return project ? `Project ${project}` : '';
}

function framework_description(source) {
  return first_present([
    source.framework.description,
    source.framework_type.description,
    'No description provided'
  ]);
}

function map_framework_status(framework = {}) {
  const is_inactive = framework.is_active === false;

  return {
    status_label: is_inactive ? 'Inactive' : 'Active',
    status_class: is_inactive ? 'is-inactive' : 'is-active'
  };
}

function build_framework_payload(source) {
  const id = source.framework.id || source.fallback_id;

  return {
    ...source.framework,
    id,
    code: `RF-${String(id).padStart(3, '0')}`,
    name: source.framework.risk_assessment || 'Untitled risk framework',
    description: framework_description(source),
    updated_label: format_date(source.framework.date_updated || source.framework.date_created),
    ...map_rule_conditions(source),
    ...map_rule_actions(source),
    ...map_framework_status(source.framework)
  };
}

function map_framework(framework = {}, index = 0) {
  return build_framework_payload(normalise_framework_source(framework, index));
}

function parseFrameworkResponse(response_data = {}) {
  if (response_data?.status === 'error') {
    throw new Error(response_data.message || 'Failed to load risk frameworks.');
  }
  return response_data?.frameworks || response_data?.data;
}

function normalizeFrameworks(frameworks) {
  return to_array(frameworks).map(map_framework);
}

export default {
  name: 'RuleEngine',
  props: {
    projectId: {
      type: Number,
      default: null
    }
  },
  data: () => ({
    frameworks: [],
    loading_frameworks: false,
    frameworks_error: ''
  }),
  computed: {
    frameworks_count() {
      return this.frameworks.length;
    }
  },
  watch: {
    projectId() {
      this.load_frameworks();
    }
  },
  mounted() {
    this.load_frameworks();
  },
  methods: {
    async fetchFrameworks() {
      return fetch_risk_frameworks_by_project(this.projectId);
    },
    assignFrameworkState(frameworks) {
      this.frameworks = normalizeFrameworks(frameworks);
    },
    handleFrameworkLoadError(error) {
      const response_data = error?.response?.data || {};
      this.frameworks = [];
      this.frameworks_error = response_data?.message
        || error?.message
        || 'Failed to load risk frameworks.';
    },
    async load_frameworks() {
      this.loading_frameworks = true;
      this.frameworks_error = '';

      try {
        const response_data = await this.fetchFrameworks();
        this.assignFrameworkState(parseFrameworkResponse(response_data));
      } catch (error) {
        this.handleFrameworkLoadError(error);
      } finally {
        this.loading_frameworks = false;
      }
    }
  }
};
